import axios from 'axios';
import Vue from 'vue';
import zenscroll from 'zenscroll';
import Errors from '../classes/errors';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    setCaptchaEuScript,
} from '../utils';
import { getFormRenderToken } from '../services/anti-bot';

Vue.component('thanks-a-million-form-component', {
    props: {
        siteHandle: {
            type: String,
        },
        captchaEuPublicKey: {
            type: String,
        },
        isCaptchaEuEnabled: {
            type: Boolean,
        },
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            formRenderToken: '',
            errors: new Errors(),
            successMessage: '',
            formCompleted: false,
            submitting: false,
        };
    },

    computed: {
        toggleLoadingButtonClass() {
            return this.submitting ? 'button--loading' : '';
        },
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        setCaptchaEuScript();

        getFormRenderToken().then((token) => {
            this.formRenderToken = token;
        });
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        handleMarketingConsentChange(e) {
            e.preventDefault();
            const isConsentChecked = e.target.checked;
            const hiddenMarketingInput = document.querySelector('#marketing');
            if (!hiddenMarketingInput) return;
            hiddenMarketingInput.setAttribute(
                'value',
                isConsentChecked ? '1' : '0',
            );
        },

        scrollToFirstError() {
            Vue.nextTick(() => {
                const firstError = document.querySelector(
                    '.new-input__container--error',
                );
                if (firstError) {
                    zenscroll.center(firstError);
                }
            });
        },

        scrollToTop() {
            Vue.nextTick(() => {
                zenscroll.toY(0);
            });
        },

        async onSubmit(event) {
            event.preventDefault();

            this.errors.clear();

            if (this.submitting) {
                return;
            }

            this.submitting = true;

            const form = event.currentTarget;

            try {
                if (!this.isCaptchaEuEnabled) {
                    await this.handleDataSubmit(form);
                    return;
                }

                const captchaSolution = await KROT.getSolution(
                    this.captchaEuPublicKey,
                );

                await this.handleDataSubmit(form, captchaSolution);
            } catch (e) {
                throw new Error(e);
            } finally {
                this.submitting = false;
            }
        },

        async handleDataSubmit(form, captchaSolution) {
            try {
                const formData = new FormData(form);
                formData.append('formRenderToken', this.formRenderToken);

                if (captchaSolution) {
                    formData.append(
                        'captchaSolution',
                        JSON.stringify(captchaSolution),
                    );
                }

                const response = await axios.post(
                    '/thanks-a-million-form/submit',
                    formData,
                );
                const result = response.data;

                if (result.data?.errors) {
                    this.errors.clear();
                    this.errors.record(result.data.errors);
                    this.scrollToFirstError();
                    this.submitting = false;
                    return;
                }

                // Success
                this.formCompleted = true;
                this.errors.clear();

                const formDataObj = {};
                for (const [key, val] of formData.entries()) {
                    formDataObj[key] = val;
                }

                this.sendGTMData({
                    event: 'lead',
                    lead_type: 'Thanks A Million',
                    country: this.siteHandle,
                    user_data: {
                        email_address: formDataObj.emailAddress,
                        phone_number: formDataObj.phone,
                        address: {
                            first_name: formDataObj.firstName,
                            last_name: formDataObj.lastName,
                        },
                    },
                });

                this.successMessage = result.data?.successMessage ?? '';

                setTimeout(() => {
                    if (result.data?.redirectUrl) {
                        window.location = result.data.redirectUrl;
                    }
                }, 1000);
            } catch (e) {
                throw new Error(e);
            }
        },

        sendGTMData(dataToPush) {
            if (!dataToPush) return;
            if (checkCCM19Consent() || checkOneTrustConsent()) {
                if (!window.dataLayer) {
                    window.dataLayer = [];
                }
                window.dataLayer.push(dataToPush);
            }
        },
    },
});
